import React, { useEffect, useState, useCallback } from 'react';
import './PromoEditor.css';
import FieldImageGallery from './FieldImageGallery';
import ImageModal from './ImageModal';

// The app builds the quest cover + quest-page gallery from images attached to
// quest_info under this legacy field type (see QuestApp get_quests_list).
const COVER_FIELD = 'promo_long_quest_description';

// Promo copy is prod-only and decoupled from the quest_data test→prod sync.
// A *_test quest and its prod twin share one promo row (keyed by prod id).
const prodId = (id) => (id && id.endsWith('_test') ? id.slice(0, -5) : id);

// Preset vocabularies. Every control still allows free-text/custom entry —
// presets are only suggestions, never a hard limit.
const FORMAT_OPTIONS = ['Квест-экскурсия', 'Аудиотур', 'Прогулка с ботом'];
const DIFFICULTY_OPTIONS = ['Лёгкая', 'Средняя', 'Высокая'];
// Only the flags the istanbul.quest product page shows. Older rows may still
// carry other keys (individual_walking, no_prepay, loyalty_discount): they are
// neither shown nor saved any more.
const SPEC_FLAGS = [
  ['offline', 'Офлайн-доступ'],
  ['instant', 'Мгновенное подтверждение'],
  ['kids_ok', 'Можно с детьми'],
];
// Headings ("## ...") of the site description with a fixed place on the
// istanbul.quest tour page. The site plugin keeps the same list
// (explorecity-site, wordpress/mu-plugins/explorecity-product-page.php,
// ec_pp_special_headings): keep the two in step. Matched case-insensitively.
const SITE_SPECIAL_HEADINGS = [
  { title: 'Как получить доступ и начать?', note: 'collapsed' },
  { title: 'Что учесть перед прогулкой?', note: 'collapsed, starts with the start point' },
  { title: 'Что входит в стоимость?', note: 'always open, before the questions; the included / not included lists come first' },
  { title: 'Если не получится решить задание?', note: 'collapsed' },
  { title: 'Можно ли перенести прогулку или вернуть деньги?', note: 'collapsed' },
];
const normalizeHeading = (s) => (s || '').replace(/\s+/g, ' ').trim().toLowerCase();
// Headings present in a Markdown text, normalized.
const headingsIn = (text) => {
  const found = new Set();
  (text || '').split(/\r\n|\r|\n/).forEach((line) => {
    const m = line.trim().match(/^#{1,3}\s+(.+)$/);
    if (m) found.add(normalizeHeading(m[1]));
  });
  return found;
};

const INCLUDED_PRESETS = [
  'Задания с подсказками',
  'Карта с геолокацией',
  'Аудиоозвучка',
  'Иллюстрации к точкам маршрута',
];
const NOT_INCLUDED_PRESETS = [
  'Личные расходы',
  'Проезд по транспорту (Istanbulkart)',
  'Входные билеты',
  'Лодочная переправа',
];

const EMPTY = {
  promo_long: '',
  promo_after: '',
  site_description: '',
  site_excerpt: '',
  specs: {},
  included: [],
  not_included: [],
  start_point: '',
  showcase: [],
};

const asArray = (v) => (Array.isArray(v) ? v : []);
// specs used to be stored as []; normalize any array/null to an object.
const asSpecsObject = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : {});
const cleanList = (arr) => asArray(arr).map((s) => (s || '').trim()).filter(Boolean);

const PromoEditor = ({ questId, canPushToProd, selectedTable, questInfoUuid, isReadOnly, onPreviewChange, onDirtyChange }) => {
  const pid = prodId(questId);
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('idle'); // idle | saving | saved | error
  const [error, setError] = useState(null);
  const [coverImages, setCoverImages] = useState([]);
  const [modalImage, setModalImage] = useState(null);
  // Result of the last "Sync to site now": the site plugin's report, verbatim.
  const [siteSync, setSiteSync] = useState({ status: 'idle', report: null });
  // What the server holds (as loaded or as last saved); anything else is unsaved.
  const [savedJson, setSavedJson] = useState(null);
  const dirty = savedJson !== null && JSON.stringify(data) !== savedJson;

  // Cover images are regular quest images (test table + test→prod sync), NOT
  // part of the straight-to-prod quest_promo row — hence the separate fetch
  // keyed by the raw questId, not the prod id.
  const refreshCoverImages = useCallback(async () => {
    if (!questId) return;
    try {
      const r = await fetch(`/api/quest-images/${questId}?table=${selectedTable || 'quests_test'}`, {
        credentials: 'include',
      });
      if (!r.ok) throw new Error('Failed to fetch quest images');
      const rows = await r.json();
      setCoverImages(rows.filter(
        (img) => img.location_id === 'quest_info' && img.field_type === COVER_FIELD
      ));
    } catch (e) {
      console.error('Error fetching cover images:', e);
    }
  }, [questId, selectedTable]);

  useEffect(() => {
    refreshCoverImages();
  }, [refreshCoverImages]);

  // Live (unsaved) promo text and the cover gallery for the app preview.
  useEffect(() => {
    if (onPreviewChange) {
      onPreviewChange({
        promoLong: data.promo_long || '',
        promoAfter: data.promo_after || '',
        coverImageIds: coverImages.map((img) => img.id),
      });
    }
  }, [data.promo_long, data.promo_after, coverImages]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!questId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(`/api/promo/${pid}`, { credentials: 'include' })
      .then((r) => {
        if (!r.ok) throw new Error('Failed to load promo');
        return r.json();
      })
      .then((d) => {
        if (!cancelled) {
          const loaded = { ...EMPTY, ...d, specs: asSpecsObject(d.specs) };
          setData(loaded);
          setSavedJson(JSON.stringify(loaded));
          setLoading(false);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e.message);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [questId, pid]);

  const ro = !canPushToProd;
  const setField = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const specs = asSpecsObject(data.specs);
  const setSpec = (k, v) => setData((d) => ({ ...d, specs: { ...asSpecsObject(d.specs), [k]: v } }));
  const setFlag = (k, on) =>
    setData((d) => {
      const s = asSpecsObject(d.specs);
      return { ...d, specs: { ...s, flags: { ...(s.flags || {}), [k]: on } } };
    });

  const save = useCallback(async () => {
    setStatus('saving');
    setError(null);
    try {
      const s = asSpecsObject(data.specs);
      const flags = {};
      SPEC_FLAGS.forEach(([k]) => { flags[k] = !!(s.flags && s.flags[k]); });
      const payload = {
        promo_long: data.promo_long || '',
        promo_after: data.promo_after || '',
        site_description: data.site_description || '',
        site_excerpt: data.site_excerpt || '',
        start_point: data.start_point || '',
        specs: {
          format: (s.format || '').trim(),
          duration: (s.duration || '').trim(),
          route_km: (s.route_km || '').trim(),
          audio_lang: (s.audio_lang || '').trim(),
          difficulty: (s.difficulty || '').trim(),
          pace: (s.pace || '').trim(),
          flags,
        },
        included: cleanList(data.included),
        not_included: cleanList(data.not_included),
        // No longer edited here; passed through so the stored value survives a save.
        showcase: cleanList(data.showcase),
      };
      const r = await fetch(`/api/promo/${pid}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.error || 'Failed to save');
      }
      setSavedJson(JSON.stringify(data));
      setStatus('saved');
      setTimeout(() => setStatus((st) => (st === 'saved' ? 'idle' : st)), 2500);
      return true;
    } catch (e) {
      setStatus('error');
      setError(e.message);
      return false;
    }
  }, [data, pid]);

  // Leaving the page with unsaved promo text: the browser asks first.
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  // The parent guards in-app quest switching with this.
  useEffect(() => {
    if (onDirtyChange) onDirtyChange(dirty);
    return () => {
      if (onDirtyChange) onDirtyChange(false);
    };
  }, [dirty]); // eslint-disable-line react-hooks/exhaustive-deps

  const syncToSite = async () => {
    // The site reads what is stored, so unsaved text is saved first.
    if (dirty) {
      const ok = await save();
      if (!ok) return;
    }
    setSiteSync({ status: 'syncing', report: null });
    try {
      const r = await fetch(`/api/site-image-sync/${pid}`, { method: 'POST', credentials: 'include' });
      const text = await r.text();
      let report;
      try {
        report = JSON.parse(text);
      } catch {
        report = { ok: false, products: [], errors: [[`Editor answered HTTP ${r.status} with non-JSON`, text.slice(-8000)]] };
      }
      if (!Array.isArray(report.errors)) {
        report = { ok: r.ok, products: report.products || [], errors: report.error ? [[report.error, '']] : [] };
      }
      setSiteSync({ status: report.ok ? 'done' : 'error', report });
    } catch (e) {
      setSiteSync({ status: 'error', report: { ok: false, products: [], errors: [[e.message, String(e.stack || '')]] } });
    }
  };

  const coverGalleryImages = coverImages.map((img) => ({
    id: img.id,
    size: img.size,
    caption: img.caption,
    url: `/api/image-file/${img.id}`,
  }));

  const handleDeleteCoverImage = async (image) => {
    if (isReadOnly || !image?.id) return;
    if (!window.confirm('Are you sure you want to delete this image?')) return;
    try {
      const r = await fetch(`/api/image-file/${image.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.error || 'Failed to delete image');
      }
      setCoverImages((prev) => prev.filter((img) => img.id !== image.id));
    } catch (e) {
      console.error('Cover image deletion error:', e);
      alert(`Failed to delete image: ${e.message}`);
    }
  };

  const handleEditCoverCaption = async (image) => {
    if (isReadOnly || !image?.id) return;
    const next = window.prompt('Image caption (leave empty to remove):', image.caption || '');
    if (next === null) return;
    try {
      const r = await fetch(`/api/image-file/${image.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ caption: next }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.error || 'Failed to update caption');
      }
      const d = await r.json();
      setCoverImages((prev) =>
        prev.map((img) => (img.id === image.id ? { ...img, caption: d.caption } : img))
      );
    } catch (e) {
      console.error('Cover caption error:', e);
      alert(`Failed to update caption: ${e.message}`);
    }
  };

  const handleReorderCoverImages = async (orderedIds) => {
    if (isReadOnly) return;
    // quest_info uuid: from props, or from any already-fetched image row
    // (the upload endpoint creates the uuid on first upload).
    const locationUuid = questInfoUuid || coverImages[0]?.location_uuid;
    if (!locationUuid) return;
    setCoverImages((prev) => {
      const byId = new Map(prev.map((img) => [img.id, img]));
      return orderedIds.map((id) => byId.get(id)).filter(Boolean);
    });
    try {
      const r = await fetch('/api/images/reorder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ locationUuid, fieldType: COVER_FIELD, orderedIds }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.error || 'Failed to reorder images');
      }
    } catch (e) {
      console.error('Cover reorder error:', e);
      refreshCoverImages();
    }
  };

  if (!questId) return null;

  // A plain multiline text field.
  const textField = (key, label, hint, rows = 3) => (
    <label className="promo-field">
      <span className="promo-field__label">{label}</span>
      {hint && <span className="promo-field__hint">{hint}</span>}
      <textarea
        className="promo-field__input"
        rows={rows}
        value={data[key] || ''}
        disabled={ro}
        onChange={(e) => setField(key, e.target.value)}
      />
    </label>
  );

  // A single-line spec input. `options` (if given) become datalist suggestions,
  // but the user can always type any value — presets never restrict the input.
  const specInput = (key, label, options) => {
    const listId = options ? `spec-${key}-list` : undefined;
    return (
      <label className="promo-spec">
        <span className="promo-spec__label">{label}</span>
        <input
          className="promo-spec__input"
          type="text"
          list={listId}
          value={specs[key] || ''}
          disabled={ro}
          onChange={(e) => setSpec(key, e.target.value)}
        />
        {options && (
          <datalist id={listId}>
            {options.map((o) => (
              <option key={o} value={o} />
            ))}
          </datalist>
        )}
      </label>
    );
  };

  // Preset checkboxes + a free-text area for custom items. Stored as a string[]:
  // checked presets first (stable order), then any custom lines.
  const checklist = (key, label, presets) => {
    const arr = asArray(data[key]);
    const checkedPresets = presets.filter((p) => arr.includes(p));
    const custom = arr.filter((x) => !presets.includes(x));
    const toggle = (p) => {
      const nextPresets = arr.includes(p)
        ? checkedPresets.filter((x) => x !== p)
        : presets.filter((x) => x === p || checkedPresets.includes(x));
      setField(key, [...nextPresets, ...custom]);
    };
    const setCustom = (text) =>
      setField(key, [...checkedPresets, ...text.split('\n')]);
    return (
      <div className="promo-field">
        <span className="promo-field__label">{label}</span>
        <div className="promo-checks">
          {presets.map((p) => (
            <label key={p} className="promo-check">
              <input
                type="checkbox"
                checked={arr.includes(p)}
                disabled={ro}
                onChange={() => toggle(p)}
              />
              <span>{p}</span>
            </label>
          ))}
        </div>
        <textarea
          className="promo-field__input"
          rows={2}
          placeholder="Custom — one per line"
          value={custom.join('\n')}
          disabled={ro}
          onChange={(e) => setCustom(e.target.value)}
        />
      </div>
    );
  };

  return (
    <div className="promo-editor">
      <div className="promo-editor__banner">
        ⚡ Promo — saved <b>straight to prod</b>, bypassing the test→prod sync
        <span className="promo-editor__qid">{pid}</span>
      </div>

      {modalImage && (
        <ImageModal
          images={modalImage.images}
          startIndex={modalImage.index}
          onClose={() => setModalImage(null)}
        />
      )}

      {loading ? (
        <div className="promo-editor__loading">Loading…</div>
      ) : (
        <>
          <div className="promo-field">
            <span className="promo-field__label">Cover & quest-page images</span>
            <span className="promo-field__hint">
              First image: cover in the app catalog and on the istanbul.quest product
              card. Whole gallery: quest page in the app and product gallery on
              istanbul.quest. Shown in the app (test and prod) as soon as uploaded;
              on the site after the sync button at the bottom.
              Images marked blur never go to the site.
            </span>
            <FieldImageGallery
              images={coverGalleryImages}
              locationId="quest_info"
              fieldType={COVER_FIELD}
              questId={questId}
              onImageUploaded={refreshCoverImages}
              onDeleteImage={handleDeleteCoverImage}
              onEditCaption={handleEditCoverCaption}
              onReorder={handleReorderCoverImages}
              onThumbnailClick={(index) =>
                setModalImage({ images: coverGalleryImages, index })
              }
              disabled={isReadOnly}
            />
          </div>

          {textField(
            'promo_long',
            'Description before purchase (promo_long)',
            'Quest page in the app while the quest is not bought yet.',
            8
          )}
          {textField(
            'promo_after',
            'Description after purchase (promo_after)',
            'Quest page in the app once the quest is bought. If empty, the before-purchase text is shown there.',
            8
          )}

          <div className="promo-field promo-site-copy">
            <span className="promo-field__label">istanbul.quest product page</span>
            <span className="promo-field__hint">
              Texts of the WooCommerce product. The site is a cache: what is typed
              here replaces the product page after the sync button at the bottom.
            </span>
            {textField(
              'site_description',
              'Site description',
              (data.site_description || '').trim()
                ? 'Markdown: "## " heading, "- " list item, "1. " numbered item, **bold**, [text](url), blank line = new paragraph.'
                : 'Empty → the site shows the before-purchase text (promo_long). Markdown: "## " heading, "- " list item, "1. " numbered item, **bold**, [text](url), blank line = new paragraph.',
              12
            )}
            {(() => {
              const found = headingsIn(data.site_description);
              return (
                <div className="promo-special-headings">
                  <span className="promo-field__hint">
                    Special "## " headings — the site places these sections itself; any other
                    heading is an ordinary open section. Highlighted = present in the text above.
                  </span>
                  <ul className="promo-special-headings__list">
                    {SITE_SPECIAL_HEADINGS.map((h) => {
                      const on = found.has(normalizeHeading(h.title));
                      return (
                        <li
                          key={h.title}
                          className={`promo-special-heading${on ? ' is-found' : ''}`}
                          title={h.note}
                        >
                          <span className="promo-special-heading__mark" aria-hidden="true">{on ? '✓' : '·'}</span>
                          <span className="promo-special-heading__title">## {h.title}</span>
                          <span className="promo-special-heading__note">{h.note}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })()}
            {textField(
              'site_excerpt',
              'Site excerpt',
              (data.site_excerpt || '').trim()
                ? 'Short text under the title and on the quest cards of the front page.'
                : 'Empty → the first paragraph of the site description is used. Short text under the title and on the quest cards of the front page.',
              3
            )}
          </div>

          <div className="promo-field">
            <span className="promo-field__label">Basic info (specs)</span>
            <span className="promo-field__hint">
              The "About this activity" block on the istanbul.quest product page.
            </span>
            <div className="promo-specs-grid">
              {specInput('format', 'Format', FORMAT_OPTIONS)}
              {specInput('difficulty', 'Difficulty', DIFFICULTY_OPTIONS)}
              {specInput('duration', 'Duration', ['4 ч', '3 ч', '2 ч'])}
              {specInput('route_km', 'Route, km')}
              {specInput('audio_lang', 'Audio language', ['Русский', 'English'])}
              {specInput('pace', 'Load (pace)', ['пешеходная', 'пешеходная, с подъёмами', 'пешком и на пароме'])}
            </div>
            <span className="promo-field__hint">
              The number of stops is not typed: the site sync counts the quest's locations.
            </span>
            <div className="promo-flags">
              {SPEC_FLAGS.map(([k, label]) => (
                <label key={k} className="promo-check">
                  <input
                    type="checkbox"
                    checked={!!(specs.flags && specs.flags[k])}
                    disabled={ro}
                    onChange={(e) => setFlag(k, e.target.checked)}
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </div>

          {checklist('included', "What's included", INCLUDED_PRESETS)}
          {checklist('not_included', "What's not included", NOT_INCLUDED_PRESETS)}

          {textField('start_point', 'Start point', 'Address / coordinates of the starting point; shown first inside "Что учесть перед прогулкой?" on the product page.', 2)}

          {canPushToProd && (
            <div className="promo-site-sync">
              <span className="promo-field__hint">
                Nothing above reaches istanbul.quest until this button is pressed (the
                price alone is also pulled hourly). It pushes this quest: price, gallery,
                product texts, specs, included / not included, start point. Save first —
                the site reads what is stored.
              </span>
              <button
                type="button"
                className="save-button promo-site-sync__button"
                disabled={siteSync.status === 'syncing'}
                onClick={syncToSite}
              >
                {siteSync.status === 'syncing'
                  ? 'Syncing…'
                  : dirty
                  ? 'Save to prod & sync to istanbul.quest'
                  : 'Sync to istanbul.quest now'}
              </button>
              {siteSync.report && (
                <div className={`promo-site-sync__result ${siteSync.report.ok ? 'ok' : 'error'}`}>
                  {(siteSync.report.products || []).map((p) => (
                    <div key={p.product_id}>
                      {p.quest_id} (product #{p.product_id}
                      {p.product_status && p.product_status !== 'publish' && `, ${p.product_status}`}):
                      {p.prices && ` price ${p.prices.status}`}
                      {p.images && `; images ${p.images.status}`}
                      {p.images && p.images.downloaded && p.images.downloaded.length > 0 && `, uploaded ${p.images.downloaded.length} new`}
                      {p.images && p.images.gallery && `, cover + ${p.images.gallery.length} in gallery`}
                      {p.content && `; content ${p.content.status}`}
                      {p.content && p.content.changed && p.content.changed.length > 0 && ` (${p.content.changed.join(', ')})`}
                      {!p.prices && !p.images && !p.content && ` ${p.status}`}
                    </div>
                  ))}
                  {(siteSync.report.errors || []).map(([msg, details], i) => (
                    <div key={i} className="promo-editor__error">
                      {msg}
                      {details && (
                        <details>
                          <summary>details</summary>
                          <pre className="promo-site-sync__details">{details}</pre>
                        </details>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="promo-editor__actions">
            <button
              type="button"
              className={`save-button promo-editor__save ${status}${dirty ? ' dirty' : ''}`}
              disabled={ro || status === 'saving'}
              onClick={save}
            >
              {status === 'saving'
                ? 'Saving…'
                : status === 'saved'
                ? 'Saved to prod ✓'
                : 'Save to prod'}
            </button>
            {dirty && <span className="promo-editor__dirty">unsaved changes</span>}
            {data.updated_at && (
              <span className="promo-editor__updated">
                updated: {new Date(data.updated_at).toLocaleString()}
              </span>
            )}
          </div>

          {error && <div className="promo-editor__error">{error}</div>}
          {ro && (
            <div className="promo-editor__error">
              No permission to write to prod (canPushToProd) — fields are read-only.
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PromoEditor;
